import { action } from '@ember/object';
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { dropTask, task } from 'ember-concurrency';

import { FacebookResponseStatus } from './types';
import { ErrorSeverity } from 'later/services/errors';
import { arrayDiff } from 'later/utils/array-methods';
import {
  InsufficientPermissionsError,
  UnknownLoginError,
  FetchPageError,
  ExtensionBlockedError
} from 'shared/errors/instagram-auth';

import type { FacebookLoginParams, FacebookPage, AccountsResponse } from './types';
import type StoreService from '@ember-data/store';
import type IntlService from 'ember-intl/services/intl';
import type SocialProfileModel from 'later/models/social-profile';
import type UserModel from 'later/models/user';
import type AlertsService from 'later/services/alerts';
import type AuthService from 'later/services/auth';
import type ErrorsService from 'later/services/errors';
import type { UnwrapAsyncRelationships } from 'shared/types';

const scopes = [
  'business_management',
  'catalog_management',
  'instagram_basic',
  'instagram_content_publish',
  'instagram_manage_comments',
  'instagram_manage_insights',
  'instagram_manage_messages',
  'instagram_shopping_tag_products',
  'pages_manage_metadata',
  'pages_manage_posts',
  'pages_messaging',
  'pages_manage_engagement',
  'pages_read_engagement',
  'pages_read_user_content',
  'pages_show_list',
  'read_insights'
] as const;

const fetchAccountsParams = {
  fields: 'picture,username,name,name_with_location_descriptor,access_token,tasks,followers_count',
  limit: 1000
} as const;

export default class FacebookAuthService extends Service {
  @service declare alerts: AlertsService;
  @service declare auth: AuthService;
  @service declare errors: ErrorsService;
  @service declare intl: IntlService;
  @service declare store: StoreService;

  @tracked authResponse?: fb.AuthResponse;
  @tracked facebookPages?: FacebookPage[];
  @tracked showFacebookSelector = false;
  @tracked socialIdentityId?: string;

  get currentUserModel(): UserModel {
    return this.auth.currentUserModel;
  }

  get facebookLoginParams(): FacebookLoginParams {
    return { return_scopes: true, scope: scopes.join(',') };
  }

  @action
  closeFacebookModal(): void {
    this.showFacebookSelector = false;
    this.facebookPages = [];
    this.authResponse = undefined;
  }

  authenticate = dropTask(
    async ({
      socialIdentityId,
      refreshSocialProfile
    }: {
      socialIdentityId?: string;
      refreshSocialProfile?: SocialProfileModel;
    }) => {
      if (this.#facebookSdkDidNotLoad()) {
        return this.#showFacebookSdkError();
      }

      this.socialIdentityId = socialIdentityId || undefined;
      this.authResponse = undefined;

      await this.#login()
        .then(async (response) => await this._handleResponse.perform(response, refreshSocialProfile))
        .catch((error) => this.#handleError(error));
    }
  );

  #facebookSdkDidNotLoad(): boolean {
    return typeof FB === 'undefined';
  }

  #showFacebookSdkError(): void {
    this.errors.log('@service:instagram-auth | Facebook Javascript SDK is unavailable.');
    this.alerts.warning(
      this.intl.t('alerts.account.controllers.groups.facebook.cant_load_sdk', {
        title: this.intl.t('shared_phrases.something_went_wrong')
      })
    );
  }

  #login(): Promise<fb.StatusResponse> {
    return new Promise((resolve, reject) => {
      FB.login((response: fb.StatusResponse) => {
        if (response.status === FacebookResponseStatus.Connected) {
          resolve(response);
        } else if (response.status === FacebookResponseStatus.Unknown) {
          reject(new UnknownLoginError(''));
        } else {
          reject(new Error(`Login error: ${response.status}`));
        }
      }, this.facebookLoginParams);
    });
  }

  _handleResponse = task(async (response: fb.StatusResponse, refreshSocialProfile?: SocialProfileModel) => {
    this.#logResponse(response);
    const declinedScopes = this.#getDeclinedScopes(response.authResponse.grantedScopes);
    if (declinedScopes.length) {
      throw new InsufficientPermissionsError(
        declinedScopes.map((declinedScope) => declinedScope.replace(/_/g, ' ')).join(', ')
      );
    } else {
      this.authResponse = response.authResponse;
      await this._saveUserModelToken.perform(response);

      if (!refreshSocialProfile) {
        this.showFacebookSelector = true;
      }
      await this._fetchFacebookPages.perform();

      if (refreshSocialProfile) {
        await this._refreshSocialProfile.perform(refreshSocialProfile);
      }
    }
  });

  #logResponse(response: fb.StatusResponse): void {
    this.errors.log(
      '@service:facebook-auth | authenticate response',
      { data: JSON.stringify(response) },
      ErrorSeverity.Debug
    );
  }

  #getDeclinedScopes(grantedScopes: string | undefined): string[] {
    return grantedScopes ? arrayDiff(scopes, grantedScopes.split(',')) : [];
  }

  _saveUserModelToken = task(async (response: fb.StatusResponse) => {
    try {
      const { userID, accessToken } = response.authResponse;
      this.currentUserModel.setProperties({
        facebookUid: userID,
        facebookToken: accessToken
      });
      this.currentUserModel.save();
    } catch (error) {
      this.errors.log('@service:facebok-auth | Unable to #saveUserModelToken', error);
      throw new Error(this.intl.t('alerts.generic_error_message'));
    }
  });

  _fetchFacebookPages = task(async () => {
    this.facebookPages = [];

    await this._fetchPages
      .perform()
      .then((facebookPages: FacebookPage[]) => (this.facebookPages = facebookPages))
      .catch((error) => this.#handleFetchPagesError(error));
  });

  _fetchPages = task(async () => {
    return new Promise((resolve, reject) => {
      FB.api('/me/accounts', fetchAccountsParams, async (accountsResponse: AccountsResponse) => {
        if (!accountsResponse) {
          reject(new FetchPageError());
        }
        if (accountsResponse.error) {
          if (accountsResponse.error.error_subcode == 1357045) {
            reject(new ExtensionBlockedError());
          }
          reject(new Error(JSON.stringify(accountsResponse.error)));
        }
        resolve(accountsResponse.data);
      });
    });
  });

  #handleFetchPagesError(error: FetchPageError | ExtensionBlockedError | Error): void {
    if (error instanceof ExtensionBlockedError) {
      this.alerts.warning(this.intl.t('errors.instagram_auth.request_blocked.message'));
    } else if (error instanceof FetchPageError) {
      this.alerts.warning(this.intl.t('errors.instagram_auth.fetch_page.message'));
    } else {
      this.alerts.warning(
        this.intl.t('errors.instagram_auth.fetch_page_subcode.message', {
          subcode: error.message
        })
      );
    }
  }

  #handleError(error: InsufficientPermissionsError | UnknownLoginError | string): void {
    if (error instanceof InsufficientPermissionsError) {
      this.alerts.warning(
        this.intl.t('errors.instagram_auth.insufficient_permissions.message', { permissions: error.message }),
        {
          title: this.intl.t('errors.instagram_auth.insufficient_permissions.title')
        }
      );
    } else if (error instanceof UnknownLoginError) {
      this.authResponse = undefined;
    } else {
      this.alerts.warning(error, { title: this.intl.t('shared_phrases.something_went_wrong') });
      this.authResponse = undefined;
    }
  }

  createSocialProfile = task(async (selectedPage: FacebookPage) => {
    if (!this.authResponse || !this.authResponse.grantedScopes || !selectedPage.id) {
      return;
    }
    const allProfiles = this.store.peekAll('social-profile');
    const socialProfile =
      allProfiles.find((profile) => profile.uid === selectedPage.id) || this.store.createRecord('social-profile');
    const socialProfileAttributes = {
      account: this.auth.currentAccount,
      avatarUrl: selectedPage.picture.data.url,
      facebookProfileType: 'page',
      group: this.auth.currentGroup,
      isFbPage: true,
      permissionScope: this.authResponse.grantedScopes.split(','),
      profileColorClass: 'fbblue',
      profileType: 'facebook',
      token: selectedPage.access_token,
      uid: selectedPage.id,
      followedBy: selectedPage.followers_count
    } satisfies Partial<UnwrapAsyncRelationships<SocialProfileModel>>;

    socialProfile.setProperties(socialProfileAttributes);

    if (this.socialIdentityId) {
      const socialIdentity = this.store.peekRecord('social-identity', this.socialIdentityId);
      socialProfile.set('socialIdentity', socialIdentity);
    }

    try {
      const savedProfile = await socialProfile.save();
      this.alerts.success(
        this.intl.t('alerts.account.controllers.groups.facebook.added', { name: savedProfile.nickname })
      );
      savedProfile.get('socialIdentity');
    } catch (error) {
      this.errors.log('@service:facebook-auth | Unable to createSocialProfile', error);
      if (error.isAdapterError && error?.errors?.[0]?.detail) {
        this.alerts.alert(error.errors[0].detail, {
          title: this.intl.t('shared_phrases.something_went_wrong')
        });
      } else {
        this.alerts.alert(error, {
          title: this.intl.t('shared_phrases.something_went_wrong')
        });
      }
      socialProfile.rollbackAttributes();
    }
  });

  _refreshSocialProfile = task(async (refreshSocialProfile: SocialProfileModel) => {
    if (!this.facebookPages || !this.authResponse || !this.authResponse.grantedScopes) {
      return;
    }

    const facebookPage = this.facebookPages?.find((facebookPage) => facebookPage.id === refreshSocialProfile.uid);

    if (facebookPage) {
      const socialProfileAttributes = {
        account: this.auth.currentAccount,
        avatarUrl: facebookPage.picture.data.url,
        group: this.auth.currentGroup,
        permissionScope: this.authResponse.grantedScopes.split(','),
        token: facebookPage.access_token,
        uid: facebookPage.id,
        followedBy: facebookPage.followers_count
      } satisfies Partial<UnwrapAsyncRelationships<SocialProfileModel>>;
      refreshSocialProfile.setProperties(socialProfileAttributes);
      refreshSocialProfile.save();
    } else {
      this.alerts.alert(
        this.intl.t('account.controllers.groups.facebook.refresh_error', {
          social_profile: refreshSocialProfile.nickname,
          loginName: ''
        })
      );
      this.errors.log(
        '@service:facebook-auth | _refreshSocialProfile',
        {
          socialProfileId: refreshSocialProfile.id,
          debug: this.authResponse?.accessToken || ''
        },
        ErrorSeverity.Debug
      );
    }
  });

  hasAllPermissions(socialProfile: SocialProfileModel): boolean {
    return scopes.every((scope) => socialProfile.permissionScope?.includes(scope));
  }
}

declare module '@ember/service' {
  interface Registry {
    'social/facebook-auth': FacebookAuthService;
  }
}
